'use client';
import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import { ThemeContext } from '@susu/custom-made/providers';
import { Clients } from '@susu/custom-made/utils/constants';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { useContext, useEffect, useState } from 'react';

import styles from './panel.module.scss';

export enum PanelSize {
  Full = 'fullSize',
  Content = 'contentSize',
  Multi = 'multiSize',
}

export enum PanelType {
  Regular = 'regular',
  MiddleScreen = 'middleScreen',
}

interface PanelProps {
  isActive: boolean;
  children?: React.ReactNode;
  panelSize?: PanelSize;
  panelType?: PanelType;
  zIndex?: number;
  withOverlay?: boolean;
  hasCloseBtn?: boolean;
  closeClick: () => void;
}

export default function Panel({
  isActive,
  children,
  panelSize = PanelSize.Full,
  panelType = PanelType.Regular,
  zIndex = 12,
  withOverlay,
  hasCloseBtn = false,
  closeClick,
}: Readonly<PanelProps>) {
  const { state } = useContext(ThemeContext);

  const webstoreHeaderHeight = state.client === Clients.onLine ? 44 : 0;
  const middleScreenOffsetY = 12 + webstoreHeaderHeight;
  const fullScreenOffsetY = 44 + webstoreHeaderHeight;
  const offsetFullScreen = panelType === PanelType.MiddleScreen ? middleScreenOffsetY : fullScreenOffsetY;
  const offsetMidScreen = 200;
  const [offsetY, setOffsetY] = useState<number>(panelSize === PanelSize.Multi ? offsetMidScreen : offsetFullScreen);
  const [isDragState, setIsDragState] = useState<boolean>(false);
  const [isAnimation, setIsAnimation] = useState<boolean>(false);
  const viewSize = useViewSize();

  useEffect(() => {
    if (!isActive) {
      setTimeout(() => {
        setIsAnimation(false);
      }, 400);
    } else {
      setIsAnimation(true);
    }
  }, [isActive]);

  const dragTouchStart = (e: React.TouchEvent) => {
    dragStart(e.touches[0].clientY);
  };

  const dragMouseStart = (e: React.MouseEvent) => {
    dragStart(e.clientY);
  };

  const dragTouch = (e: TouchEvent) => {
    drag(e.touches[0].clientY);
  };

  const dragMouse = (e: MouseEvent) => {
    drag(e.clientY);
  };

  const dragTouchEnd = (e: TouchEvent) => {
    dragEnd(e.changedTouches[0].clientY);
  };

  const dragMouseEnd = (e: MouseEvent) => {
    dragEnd(e.clientY);
  };

  const dragStart = (clientY: number) => {
    document.documentElement.style.overscrollBehavior = 'none';
    setIsDragState(true);
    setOffsetY(clientY);
    document.addEventListener('mouseup', dragMouseEnd);
    document.addEventListener('mousemove', dragMouse);
    document.addEventListener('touchend', dragTouchEnd);
    document.addEventListener('touchmove', dragTouch);
  };

  const drag = (clientY: number) => {
    let coorY = clientY;
    if (coorY < offsetFullScreen) {
      coorY = offsetFullScreen;
    }
    setOffsetY(coorY);
  };

  const dragEnd = (clientY: number) => {
    let coorY = clientY;
    document.removeEventListener('mouseup', dragMouseEnd);
    document.removeEventListener('mousemove', dragMouse);
    document.removeEventListener('touchend', dragTouchEnd);
    document.removeEventListener('touchmove', dragTouch);

    if (panelSize === PanelSize.Multi) {
      if (coorY > offsetMidScreen + 80 && coorY > offsetY) {
        closeClick();
        setOffsetY(offsetMidScreen);
      } else if (coorY < offsetMidScreen + 80 && coorY > offsetY) {
        setOffsetY(offsetMidScreen);
      } else if (coorY < offsetY) {
        setOffsetY(offsetFullScreen);
      }
    } else if (coorY > offsetY) {
      setOffsetY(offsetFullScreen);
      closeClick();
    }

    document.documentElement.style.overscrollBehavior = '';
    setIsDragState(false);
  };

  const setTop = (panelSize: PanelSize) => {
    return panelSize === PanelSize.Multi ? '100%' : '';
  };

  const styleOverlayTransparent = {
    background: 'transparent',
    zIndex: isActive ? zIndex : -1,
  };

  const styleOverlayDefault = { zIndex: isActive ? zIndex : -1 };

  return (
    <div
      className={cn(styles.panelContainer, styles[panelType], {
        [styles.isActive]: isActive,
        [styles.inStore]: state.client === Clients.inStore,
      })}
    >
      <div
        className={cn(styles.panelOverlay, {
          [styles.withOverlay]: withOverlay,
        })}
        style={
          ((offsetY > offsetFullScreen && panelSize !== PanelSize.Full) && viewSize === ViewSize.Mobile) ? styleOverlayTransparent : styleOverlayDefault
        }
        onClick={closeClick}
        onKeyDown={e => {
          onEnter(e, () => {
            closeClick();
          });
        }}
      ></div>
      <div
        className={cn(styles.panelContent, styles[panelSize], styles[panelType], {
          [styles.isAnimationActive]: isAnimation,
          [styles.isDragActive]: isDragState,
        })}
        data-test-id="panel-content"
        style={
          viewSize === ViewSize.Mobile
            ? {
                zIndex: isActive ? zIndex : 'unset',
                top: isActive && (panelSize !== PanelSize.Content || isDragState) ? offsetY + 'px' : setTop(panelSize),
                transition: isDragState
                  ? 'none'
                  : 'max-height 0.4s cubic-bezier(0.5, 0.05, 0.1, 1), top 0.4s cubic-bezier(0.5, 0.05, 0.1, 1)',
              }
            : { zIndex: zIndex }
        }
      >
        <div className={styles.barContainer} onMouseDown={dragMouseStart} onTouchStart={dragTouchStart}></div>
        {hasCloseBtn && (
          <div className={styles.closeIconContainer} onClick={closeClick} data-test-id="panel-close-btn">
            <i className="susu-icons" translate="no">
              close
            </i>
          </div>
        )}
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
}
